import React, { useState, useEffect } from "react";
import {
  Box,
  Flex,
  Text,
  Icon,
  chakra,
  shouldForwardProp,
  VStack,
  Divider,
} from "@chakra-ui/react";
import { IoIosInformationCircleOutline } from "react-icons/io";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { useSelector } from "react-redux";
import { useParams } from "react-router";

const ChakraBox = chakra(motion.div, {
  shouldForwardProp: (prop) => shouldForwardProp(prop) || prop === "transition",
});

const BetLimitInfo = ({ betLimitInfo }) => {
  const { t } = useTranslation();
  const i18n = (key) => t(`gamebox.${key}`);
  const { to: platform } = useParams();

  const isHandleAllGame = useSelector((state) => state.isHandleAllGame);
  const sortedByPlatformGames = isHandleAllGame?.sortedByPlatformGames;

  const [visible, setVisible] = useState(true);

  // Reset visibility when betLimitInfo changes
  useEffect(() => {
    if (betLimitInfo) {
      setVisible(true);
    }
  }, [betLimitInfo]);

  if (!betLimitInfo) return null;

  const isArray = Array.isArray(betLimitInfo);
  const dataList = isArray ? betLimitInfo : [betLimitInfo];

  const getGameName = (gameCode) => {
    if (!gameCode || !sortedByPlatformGames || !platform) return "";
    const platformGames = sortedByPlatformGames[platform] || [];
    // Search by gameCode or any other possible field if necessary
    const game = platformGames.find((item) => item.gameCode === gameCode);

    return game?.gameNameTw || "";
  };

  return (
    <AnimatePresence>
      {visible && (
        <ChakraBox
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 20 }}
          position="absolute"
          top="60px"
          right="10px"
          zIndex={1000}
          bg="blackAlpha.800"
          backdropFilter="blur(8px)"
          px="4"
          py="3"
          borderRadius="xl"
          color="white"
          fontSize="sm"
          display="flex"
          alignItems="flex-start"
          gap="3"
          cursor="pointer"
          onClick={() => setVisible(false)}
          boxShadow="0 4px 20px rgba(0,0,0,0.5)"
          border="1px solid"
          borderColor="whiteAlpha.300"
          maxW="280px"
          maxH="400px"
          overflowY="auto"
          className="scrollable"
          _hover={{
            bg: "blackAlpha.900",
            borderColor: "brand.200",
          }}
          transition={{ duration: 0.2 }}
        >
          <Icon
            as={IoIosInformationCircleOutline}
            fontSize="2xl"
            color="yellow.400"
            mt="0.5"
          />
          <VStack align="stretch" spacing="3" flex="1">
            {dataList.map((item, index) => {
              const gameName = getGameName(item.game_code);
              return (
                <Box key={index}>
                  {isArray && gameName && (
                    <Text
                      fontSize="sm"
                      fontWeight="bold"
                      color="yellow.400"
                      mb="1.5"
                      lineHeight="shorter"
                    >
                      {gameName}
                    </Text>
                  )}
                  <Flex direction="column" gap="1.5">
                    <Flex align="center" gap="4" justifyContent="space-between">
                      <Text opacity={0.8} fontSize="md" fontWeight="medium">
                        {i18n("minBet")}
                      </Text>
                      <Text fontSize="lg" fontWeight="bold" color="brand.200">
                        {item.min}
                      </Text>
                    </Flex>
                    <Flex align="center" gap="4" justifyContent="space-between">
                      <Text opacity={0.8} fontSize="md" fontWeight="medium">
                        {i18n("maxBet")}
                      </Text>
                      <Text fontSize="lg" fontWeight="bold" color="brand.200">
                        {item.max}
                      </Text>
                    </Flex>
                  </Flex>
                  {isArray && index < dataList.length - 1 && (
                    <Divider mt="3" borderColor="whiteAlpha.300" />
                  )}
                </Box>
              );
            })}
          </VStack>
        </ChakraBox>
      )}
    </AnimatePresence>
  );
};

export default BetLimitInfo;
